import React, { useState, useCallback, useEffect, useRef } from 'react';
import { generatePath } from 'react-router-dom';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
} from '@microsoft/signalr';
import { useOktaAuth } from '@okta/okta-react';
import { DisplayData } from 'src/pages/Solutions/SolutionAssetHierarchy/SolutionAssetHierarchy';
import { ContentType, pepApiRequest, RequestMethod } from 'src/services';
import { generateApiPath } from 'src/utils';
import {
  API_DIGITALTWIN_IMAGE_PROPERTY,
  API_GET_DIGITALTWIN_IMAGE,
} from 'src/pages/Solutions/constants';
import TempConfigureTab from './TempConfigureTab';
import TempLiveMonitorTab from './TempLiveMonitorTab';
import { PageCardLoader } from '../PageCardLayout';
import { AlertType, useAlert } from '../AlertsProvider';

export interface Property {
  propertyName: string | undefined;
  propertyKey: string | undefined;
  propertyValue: string;
  dataType: string | undefined;
  xCoordinate: number;
  yCoordinate: number;
  visible: boolean | undefined;
}

export interface UploadedImageProperty {
  imageName: string | null;
  description: string;
  properties: Property[];
}

export interface PlacedProperty extends DisplayData {
  x: number;
  y: number;
}

interface ImagePropertyMapperProps {
  displayData: DisplayData[];
  twinId: string;
  solutionId: string;
  siteId: string;
}

const TempImagePropertyMapper: React.FC<ImagePropertyMapperProps> = ({
  displayData,
  twinId,
  solutionId,
  siteId,
}) => {
  const { showAlert } = useAlert();
  const { oktaAuth } = useOktaAuth();
  const [isConfigureDialogOpen, setIsConfigureDialogOpen] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedImageName, setUploadedImageName] = useState<string | null>(
    null
  );
  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const signalRUrl = generatePath('/proxy/signalr/eventshub');
  const [availableProperties, setAvailableProperties] =
    useState<DisplayData[]>(displayData);
  const [placedProperties, setPlacedProperties] = useState<PlacedProperty[]>(
    []
  );
  const [isSave, setIsSave] = useState<boolean>(false);
  const [loader, setLoader] = useState(false);
  const [uploadedImageProperty, setUploadedImageProperty] =
    useState<UploadedImageProperty>({
      imageName: '',
      description: '',
      properties: [],
    });
  const [propertyId, setPropertyId] = useState<string>('');
  const visibilitySyncedRef = useRef(false);
  const prevDisplayDataRef = useRef<DisplayData[]>(displayData);

   // Sync availableProperties when displayData prop updates (e.g. after API fetch or twin change)
  useEffect(() => {
    if (displayData.length > 0) {
      const hasChanged =
        displayData.length !== prevDisplayDataRef.current.length ||
        displayData.some(
          (item, idx) => item.name !== prevDisplayDataRef.current[idx]?.name
        );
      if (hasChanged || availableProperties.length === 0) {
        setAvailableProperties(displayData);
        setPlacedProperties([]);
        visibilitySyncedRef.current = false;
      }
      prevDisplayDataRef.current = displayData;
    }
  }, [displayData]);
  
  // Snapshots for cancel functionality
  const snapshotRef = useRef<{
    placedProperties: PlacedProperty[];
    availableProperties: DisplayData[];
    uploadedImage: string | null;
    uploadedImageName: string | null;
  } | null>(null);

  const connectionRef = useRef<HubConnection | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  const connectToSignalR = useCallback(async () => {
    console.log('[LiveMonitor] connectToSignalR called', {
      signalRUrl,
      twinId,
      solutionId,
    });

    try {
      if (connectionRef.current) {
        await connectionRef.current.stop();
        connectionRef.current = null;
      }

      const newConnection = new HubConnectionBuilder()
        .withUrl(signalRUrl, { accessTokenFactory: () => oktaAuth.getAccessToken() || '' })
        .withAutomaticReconnect([0, 2000, 10000])
        .configureLogging(LogLevel.Information)
        .build();

      const filter = {
        SolutionIds: [solutionId],
      };

      newConnection.onreconnecting((error) => {
        console.log('[LiveMonitor] SignalR reconnecting...', error);
        setIsConnected(false);
      });

      newConnection.onreconnected(async () => {
        console.log('[LiveMonitor] SignalR reconnected, re-subscribing...');
        try {
          await newConnection.invoke('Subscribe', filter);
          console.log('[LiveMonitor] Re-subscribed successfully');
          setIsConnected(true);
        } catch (err) {
          console.error('[LiveMonitor] Re-subscribe failed:', err);
          setIsConnected(false);
        }
      });

      newConnection.onclose((error) => {
        console.log('[LiveMonitor] SignalR connection closed permanently', error);
        setIsConnected(false);
        // Connection is dead — rebuild from scratch after a delay
        if (isMountedRef.current) {
          console.log('[LiveMonitor] Will attempt full reconnect in 5s...');
          reconnectTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connectToSignalR();
            }
          }, 5000);
        }
      });

      newConnection.on('new-event', (message) => {
        const messageJson = JSON.parse(message);
        if (messageJson.twinId === twinId) {
          setAvailableProperties((prev) =>
            prev.map((prop) =>
              Object.hasOwn(messageJson, prop.name || '')
                ? { ...prop, value: messageJson[prop.name || ''] }
                : prop
            )
          );
          setPlacedProperties((prev) =>
            prev.map((prop) =>
              Object.hasOwn(messageJson, prop.name || '')
                ? { ...prop, value: messageJson[prop.name || ''] }
                : prop
            )
          );
        }
      });

      await newConnection.start();
      console.log('[LiveMonitor] SignalR connected, subscribing...');
      await newConnection.invoke('Subscribe', filter);
      console.log('[LiveMonitor] Subscribed successfully');
      connectionRef.current = newConnection;
      setConnection(newConnection);
      setIsConnected(true);
    } catch (error) {
      console.error('[LiveMonitor] Connection failed:', error);
      setIsConnected(false);
      // Retry from scratch after a delay
      if (isMountedRef.current) {
        console.log('[LiveMonitor] Will retry connection in 5s...');
        reconnectTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectToSignalR();
          }
        }, 5000);
      }
    }
  }, [signalRUrl, solutionId, twinId, oktaAuth]);

  useEffect(() => {
    isMountedRef.current = true;
    connectToSignalR();

    return () => {
      isMountedRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (connectionRef.current) {
        connectionRef.current.stop();
        setIsConnected(false);
      }
    };
  }, [connectToSignalR]);

  useEffect(() => {
    // Reset sync flag when twinId changes
    visibilitySyncedRef.current = false;
    
    pepApiRequest({
      method: RequestMethod.Get,
      url: generateApiPath(API_DIGITALTWIN_IMAGE_PROPERTY, {
        solutionId,
        siteId,
        twinId,
      }),
    }).then(
      (response) => {
        if (response.id) {
          setPropertyId(response.id);
          setUploadedImageProperty(response);
          const propertyPlaced = response.properties.map((res) => {
            const property: PlacedProperty = {
              name: res.propertyName,
              key: res.propertyKey,
              schema: res.dataType,
              value: res.propertyValue,
              visible: res.visible,
              x: res.xCoordinate,
              y: res.yCoordinate,
            };
            return property;
          });
          setPlacedProperties(propertyPlaced);
          pepApiRequest({
            method: RequestMethod.Get,
            url: generateApiPath(API_GET_DIGITALTWIN_IMAGE, {
              solutionId,
              siteId,
              twinId,
              imageName: response.imageName,
            }),
          }).then((response) => {
            const resStream = new Response(response.body);
            resStream.arrayBuffer().then((res) => {
              const bytes = new Uint8Array(res);
              const base64 = btoa(
                Array.from(bytes, (byte) => String.fromCharCode(byte)).join('')
              );
              setUploadedImage(`data:image/jpeg;base64,${base64}`);
            });
          });
        }
      },
      (err) => {
        setUploadedImage('');
        setUploadedImageProperty({
          imageName: '',
          description: '',
          properties: [],
        });
        setPlacedProperties([]);
      }
    );
  }, [twinId]);

  // Sync visibility from backend loaded placedProperties to availableProperties (one time only)
  useEffect(() => {
    if (!visibilitySyncedRef.current && placedProperties.length > 0) {
      setAvailableProperties((prev) =>
        prev.map((availProp) => {
          const placedProp = placedProperties.find(
            (pp) => pp.key === availProp.key
          );
          if (placedProp && placedProp.visible !== undefined) {
            return { ...availProp, visible: placedProp.visible };
          }
          return availProp;
        })
      );
      visibilitySyncedRef.current = true;
    }
  }, [placedProperties]);

  useEffect(() => {
    if (isSave) {
      setLoader(true);
      const mappedPlacedProperties = placedProperties.map((property) => ({
        PropertyName: property.name || undefined,
        propertyKey: property.key || undefined,
        propertyValue: property.value,
        dataType: property.schema || undefined,
        xCoordinate: property.x,
        yCoordinate: property.y,
        visible: property.visible,
      }));
      const property = {
        imageName:
          uploadedImageName !== null
            ? uploadedImageName
            : uploadedImageProperty.imageName,
        description: `${
          uploadedImageName !== null
            ? uploadedImageName
            : uploadedImageProperty.imageName
        } image for asset`,
        properties: mappedPlacedProperties,
      };
      if (propertyId) {
        pepApiRequest({
          method: RequestMethod.Put,
          url: generateApiPath(API_DIGITALTWIN_IMAGE_PROPERTY, {
            solutionId,
            siteId,
            twinId,
          }),
          contentType: ContentType.JSON,
          params: property,
        }).then(
          (response) => {
            showAlert({
              type: AlertType.Info,
              title: 'Image configuration modified',
              text: 'Image configuration has been updated successfully.',
            });
            setLoader(false);
          },
          (err) => {
            setLoader(false);
          }
        );
      } else {
        pepApiRequest({
          method: RequestMethod.Post,
          url: generateApiPath(API_DIGITALTWIN_IMAGE_PROPERTY, {
            solutionId,
            siteId,
            twinId,
          }),
          contentType: ContentType.JSON,
          params: property,
        }).then(
          (response) => {
            setPropertyId(response.id);
            showAlert({
              type: AlertType.Info,
              title: 'Image configuration saved',
              text: 'Image configuration has been saved successfully.',
            });
            setLoader(false);
          },
          (err) => {
            setLoader(false);
          }
        );
      }
      setIsSave(false);
    }
  }, [isSave, twinId, propertyId]);

  useEffect(() => {
    setPlacedProperties((prev) =>
      prev.map((placedProp) => {
        const updatedAvailableProp = availableProperties.find(
          (ap) => ap.key === placedProp.key
        );
        return updatedAvailableProp
          ? { ...placedProp, value: updatedAvailableProp.value }
          : placedProp;
      })
    );
  }, [availableProperties]);

  const handleImageUpload = useCallback(
    (imageUrl: string, imageName: string) => {
      setUploadedImage(imageUrl);
      setUploadedImageName(imageName);
    },
    []
  );

  const handlePropertyVisibilityToggle = useCallback((propertyKey: string) => {
    setAvailableProperties((prev) =>
      prev.map((prop) =>
        prop.key === propertyKey ? { ...prop, visible: !prop.visible } : prop
      )
    );

    setPlacedProperties((prev) =>
      prev.map((prop) =>
        prop.key === propertyKey ? { ...prop, visible: !prop.visible } : prop
      )
    );
  }, []);

  const handlePropertyDrop = useCallback(
    (propertyKey: string, x: number, y: number) => {
      const property = availableProperties.find(
        (prop) => prop.key === propertyKey
      );
      if (
        property &&
        !placedProperties.find((placed) => placed.key === propertyKey)
      ) {
        const newPlacedProperty: PlacedProperty = {
          ...property,
          visible: property.visible !== false,
          x,
          y,
        };
        setPlacedProperties((prev) => [...prev, newPlacedProperty]);
      }
    },
    [availableProperties, placedProperties]
  );

  const handlePlacedPropertyRemove = useCallback((propertyKey: string) => {
    setPlacedProperties((prev) =>
      prev.filter((prop) => prop.key !== propertyKey)
    );
  }, []);

  const handleClearAll = useCallback(() => {
    setPlacedProperties([]);
  }, []);

  const handleOpenConfigureDialog = useCallback(() => {
    // Save snapshot of current state for cancel functionality
    snapshotRef.current = {
      placedProperties: JSON.parse(JSON.stringify(placedProperties)),
      availableProperties: JSON.parse(JSON.stringify(availableProperties)),
      uploadedImage,
      uploadedImageName,
    };
    setIsConfigureDialogOpen(true);
  }, [placedProperties, availableProperties, uploadedImage, uploadedImageName]);

  const handleCloseConfigureDialog = useCallback(() => {
    // Restore snapshot on cancel
    if (snapshotRef.current) {
      setPlacedProperties(snapshotRef.current.placedProperties);
      setAvailableProperties(snapshotRef.current.availableProperties);
      setUploadedImage(snapshotRef.current.uploadedImage);
      setUploadedImageName(snapshotRef.current.uploadedImageName);
      snapshotRef.current = null;
    }
    setIsConfigureDialogOpen(false);
  }, []);

  const handleSaveConfiguration = useCallback(() => {
    // Clear snapshot on save
    snapshotRef.current = null;
    setIsSave(true);
    setIsConfigureDialogOpen(false);
  }, []);

  const handlePropertyMove = useCallback(
    (propertyKey: string, x: number, y: number) => {
      setPlacedProperties((prev) =>
        prev.map((prop) =>
          prop.key === propertyKey ? { ...prop, x, y } : prop
        )
      );
    },
    []
  );

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    flexDirection: 'column',
  };

  const tabPanelStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'auto',
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={tabPanelStyle}>
        <TempLiveMonitorTab
          uploadedImage={uploadedImage}
          availableProperties={availableProperties}
          placedProperties={placedProperties}
          onOpenConfigure={handleOpenConfigureDialog}
          isConnected={isConnected}
        />
      </Box>

      <Dialog
        open={isConfigureDialogOpen}
        onClose={handleCloseConfigureDialog}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            maxWidth: '95vw',
            height: '90vh',
            maxHeight: '90vh',
            m: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            pb: 2,
          }}
        >
          Configure Properties
          <IconButton
            onClick={handleCloseConfigureDialog}
            size="small"
            sx={{ color: '#666' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
          <TempConfigureTab
            uploadedImage={uploadedImage}
            availableProperties={availableProperties}
            placedProperties={placedProperties}
            onImageUpload={handleImageUpload}
            onPropertyVisibilityToggle={handlePropertyVisibilityToggle}
            onPropertyDrop={handlePropertyDrop}
            onPlacedPropertyRemove={handlePlacedPropertyRemove}
            onPropertyMove={handlePropertyMove}
            solutionId={solutionId}
            siteId={siteId}
            twinId={twinId}
            onClearAll={handleClearAll}
            isDialog={true}
          />
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid #e0e0e0',
            px: 3,
            py: 2,
            gap: 1,
          }}
        >
          <Button
            onClick={handleCloseConfigureDialog}
            variant="outlined"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveConfiguration}
            variant="contained"
            sx={{ textTransform: 'none' }}
          >
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>
      {loader && <PageCardLoader />}
    </Box>
  );
};

export default TempImagePropertyMapper;
